import { useMemo } from 'react';
import { css } from '@emotion/react';
import { RRule, Weekday } from 'rrule';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { DatePickerField, DaysOfWeek, DropdownField, Text, TimeField } from '@frontend/design-system';
import { useRuleRecurrenceForm } from '../../../../hooks/settings/use-rule-recurrence-form';
import { getRRuleDayFromDaysOfWeek } from '../../../../rrule-helper';
import { AvailabilityFormRow } from '../../availability-form/atoms/availability-form-row';
import { AvailabilityFormSection } from '../../availability-form/atoms/availability-form-section';
import { DayPickerWithSelectedDays } from '../../availability-form/atoms/day-picker-with-selected-days';
import { MonthlyRecurrenceRow } from '../../availability-form/atoms/monthly-recurrence-row';
import { PreviewNotification } from '../../availability-form/atoms/preview-notification';
import { RepeatFrequencyDropdown } from '../../availability-form/atoms/repeat-frequency-dropdown';
import { getPreviewPatternDescription } from '../../availability-form/helpers/get-preview-pattern-description';
import {
  availabilityRecurrenceOptions,
  AvailabilityRuleFormValues,
  GetAvailabilityRuleFormFieldProps,
  repeatFrequencyOptions,
} from '../../availability-form/types';

export type RRuleHandler = (rRule: RRule | undefined) => void;

type RuleRecurrenceFormProps = {
  locationId: string | undefined;
  ruleRecurrenceFormProps: ReturnType<
    typeof useRuleRecurrenceForm<GetAvailabilityRuleFormFieldProps, AvailabilityRuleFormValues>
  >;
};

export const RuleRecurrenceForm = ({ locationId, ruleRecurrenceFormProps }: RuleRecurrenceFormProps) => {
  const { t } = useTranslation('schedule-settings');

  const { locationTimezoneMap } = SchedulerV3.Helpers.useScheduleLocationTimezoneShallowStore('locationTimezoneMap');
  const timezone = locationTimezoneMap[locationId ?? '']?.timezone;

  const {
    recurrenceFormValues,
    recurrenceType,
    daysOfWeek,
    repeatOn,
    rRule,
    formValues,
    setDaysOfWeek,
    setRepeatOn,
    getRecurrenceFormFieldProps,
    getMonthlyRecurrenceFormFieldProps,
    getAvailabilityRuleFieldProps,
  } = ruleRecurrenceFormProps;

  const { repeatFrequencyType } = recurrenceFormValues;
  const handleDayToggle =
    (setState: React.Dispatch<React.SetStateAction<Weekday[]>>) =>
    ({ selectedDays }: { selectedDays: DaysOfWeek[] }) => {
      setState(selectedDays.map(getRRuleDayFromDaysOfWeek));
    };
  const previewPatternDescription = useMemo(
    () =>
      getPreviewPatternDescription({
        recurrenceType,
        repeatFrequencyType,
        rRule,
        formValues,
        timezone,
      }),
    [recurrenceType, repeatFrequencyType, rRule, formValues, timezone]
  );

  return (
    <AvailabilityFormSection title={t('Schedule')}>
      <AvailabilityFormRow>
        <DropdownField label={t('Recurrence')} {...getAvailabilityRuleFieldProps('recurrenceType')} hasNonFloatingLabel>
          {availabilityRecurrenceOptions?.map((item) => (
            <DropdownField.Option key={item.id} value={item.id}>
              {item.displayName}
            </DropdownField.Option>
          ))}
        </DropdownField>
      </AvailabilityFormRow>
      {recurrenceType === 'SPECIFIC_DATE' && (
        <AvailabilityFormRow>
          <DatePickerField
            label={t('Date')}
            {...getAvailabilityRuleFieldProps('startDate')}
            hasNonFloatingLabel
            css={{ maxWidth: 'none' }}
          />
        </AvailabilityFormRow>
      )}
      {recurrenceType === 'WEEKLY' && (
        <AvailabilityFormRow title={t('Day(s) of the week')}>
          <DayPickerWithSelectedDays daysPicked={daysOfWeek} onDayToggle={handleDayToggle(setDaysOfWeek)} />
        </AvailabilityFormRow>
      )}
      {recurrenceType === 'CUSTOM' && (
        <>
          <AvailabilityFormRow title={t('Repeat Pattern')} css={repeatPatternRowStyles}>
            <Text>{t('Every')}</Text>
            <RepeatFrequencyDropdown getFieldProps={getRecurrenceFormFieldProps} />
            <DropdownField label={''} {...getRecurrenceFormFieldProps('repeatFrequencyType')}>
              {repeatFrequencyOptions?.map((item) => (
                <DropdownField.Option key={item.id} value={item.id}>
                  {item.displayName}
                </DropdownField.Option>
              ))}
            </DropdownField>
          </AvailabilityFormRow>
          {recurrenceFormValues.repeatFrequencyType === 'weeks' && (
            <AvailabilityFormRow title={t('Repeat On')}>
              <DayPickerWithSelectedDays daysPicked={repeatOn} onDayToggle={handleDayToggle(setRepeatOn)} />
            </AvailabilityFormRow>
          )}
          {recurrenceFormValues.repeatFrequencyType === 'months' && (
            <MonthlyRecurrenceRow getFieldProps={getMonthlyRecurrenceFormFieldProps} />
          )}
        </>
      )}
      {recurrenceType !== 'SPECIFIC_DATE' && (
        <AvailabilityFormRow>
          <DatePickerField
            label={t('Start Date')}
            {...getAvailabilityRuleFieldProps('startDate')}
            hasNonFloatingLabel
          />
          <DatePickerField
            label={<DayPickerLabel />}
            {...getAvailabilityRuleFieldProps('endDate')}
            hasNonFloatingLabel
          />
        </AvailabilityFormRow>
      )}
      <AvailabilityFormRow>
        <TimeField label={t('Start Time')} {...getAvailabilityRuleFieldProps('startTime')} hasNonFloatingLabel />
        <TimeField label={t('End Time')} {...getAvailabilityRuleFieldProps('endTime')} hasNonFloatingLabel />
      </AvailabilityFormRow>
      {recurrenceType === 'CUSTOM' && (
        <AvailabilityFormRow>
          <PreviewNotification title={t('Pattern preview:')} description={previewPatternDescription} />
        </AvailabilityFormRow>
      )}
    </AvailabilityFormSection>
  );
};

const DayPickerLabel = () => {
  const { t } = useTranslation('schedule-settings');
  return (
    <>
      {t('End Date')}
      <em style={{ fontWeight: theme.font.weight.regular, color: theme.font.colors.subdued }}> ({t('optional')})</em>
    </>
  );
};

const repeatPatternRowStyles = css({
  display: 'flex',
  alignItems: 'center',

  '> *': {
    flexBasis: 0,
    flexShrink: 1,
  },
  '> *:last-child': {
    flex: 1,
  },
});
